import { useMemo } from 'react';
import clsx from 'clsx';
import { ArrowDownCircle, ArrowUpCircle, Clock, ChevronLeft, ChevronRight, Paperclip } from 'lucide-react';
import { Card, StatusBadge } from '@components/ui';

export default function CeklerTablosu({
    paginatedChecks,
    filteredChecks,
    currentPage,
    setCurrentPage,
    totalPages,
    ITEMS_PER_PAGE,
    onRowClick,
    getStatusBadge,
    getDueDateClass,
    formatCurrency,
    formatDate,
    getBankLabel
}) {
    if (paginatedChecks.length === 0) {
        return (
            <Card padding="lg" className="text-center">
                <p className="text-text-muted">Bu kriterlerde çek bulunamadi.</p>
            </Card>
        );
    }

    return (
        <Card padding="none" className="overflow-hidden">
            <div className="overflow-x-auto">
                <table className="w-full">
                    <thead>
                        <tr className="bg-bg-tertiary border-b border-border-light">
                            <th className="text-left py-3 px-4 text-xs font-semibold text-text-secondary uppercase">Çek No</th>
                            <th className="text-left py-3 px-4 text-xs font-semibold text-text-secondary uppercase">Tür</th>
                            <th className="text-left py-3 px-4 text-xs font-semibold text-text-secondary uppercase">Kime/Kimden</th>
                            <th className="text-left py-3 px-4 text-xs font-semibold text-text-secondary uppercase hidden lg:table-cell">Banka</th>
                            <th className="text-left py-3 px-4 text-xs font-semibold text-text-secondary uppercase">Vade</th>
                            <th className="text-right py-3 px-4 text-xs font-semibold text-text-secondary uppercase">Tutar</th>
                            <th className="text-center py-3 px-4 text-xs font-semibold text-text-secondary uppercase">Durum</th>
                        </tr>
                    </thead>
                    <tbody>
                        {paginatedChecks.map((check, idx) => {
                            const statusBadge = getStatusBadge(check.status);
                            const name = check.type === 'alinan' ? check.customer : check.recipient;
                            return (
                                <tr
                                    key={check.id}
                                    onClick={() => onRowClick(check)}
                                    className={clsx(
                                        "border-b border-border-light hover:bg-bg-hover transition-colors cursor-pointer",
                                        idx % 2 === 0 ? "bg-bg-secondary" : "bg-bg-primary"
                                    )}
                                >
                                    <td className="py-3 px-4">
                                        <div className="flex flex-col">
                                            <span className="text-sm font-mono text-text-secondary">{check.checkNo}</span>
                                            {/* ERP İLİŞKİSİ GÖSTERGELERİ */}
                                            {check.linked_scheduled_payment_id && (
                                                <span className="text-[10px] text-primary/70 truncate flex items-center gap-1 mt-0.5">
                                                    🔗 Borca Bağlı
                                                </span>
                                            )}
                                            {check.linked_sale_installment_id && (
                                                <span className="text-[10px] text-success/70 truncate flex items-center gap-1 mt-0.5">
                                                    🔗 Taksite Bağlı
                                                </span>
                                            )}
                                            {check.document_url && (
                                                <span className="text-[10px] text-text-muted flex items-center gap-1 mt-0.5">
                                                    <Paperclip size={10} /> Evrak Var
                                                </span>
                                            )}
                                        </div>
                                    </td>
                                    <td className="py-3 px-4">
                                        <span className={clsx(
                                            "inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium",
                                            check.type === 'verilen'
                                                ? 'bg-error/10 text-error'
                                                : 'bg-success/10 text-success'
                                        )}>
                                            {check.type === 'verilen' ? <ArrowUpCircle size={12} /> : <ArrowDownCircle size={12} />}
                                            {check.type === 'verilen' ? 'Verilen' : 'Alınan'}
                                        </span>
                                    </td>
                                    <td className="py-3 px-4">
                                        <div>
                                            <p className="text-text-primary font-medium text-sm">{name}</p>
                                            {check.apartment && (
                                                <p className="text-xs text-text-muted">{check.apartment}</p>
                                            )}
                                            {/* Ciro Bildirimi */}
                                            {check.status === 'ciro_edildi' && (
                                                <p className="text-xs text-warning-dark mt-0.5">↪️ Ciro: {check.supplierId}</p>
                                            )}
                                        </div>
                                    </td>
                                    <td className="py-3 px-4 hidden lg:table-cell">
                                        <span className="text-sm text-text-secondary">{getBankLabel(check.bank)}</span>
                                    </td>
                                    <td className={clsx("py-3 px-4 text-sm", getDueDateClass(check.dueDate, check.status))}>
                                        {formatDate(check.dueDate)}
                                    </td>
                                    <td className={clsx(
                                        "py-3 px-4 text-right font-bold text-sm",
                                        check.type === 'verilen' ? 'text-error' : 'text-success'
                                    )}>
                                        {formatCurrency(check.amount)}
                                    </td>
                                    <td className="py-3 px-4 text-center">
                                        <span className={clsx(
                                            "inline-flex px-2.5 py-1 rounded-full text-xs font-medium text-white",
                                            statusBadge.color
                                        )}>
                                            {statusBadge.label}
                                        </span>
                                    </td>
                                </tr>
                            );
                        })}
                    </tbody>
                </table>
            </div>

            {/* Pagination */}
            {totalPages > 1 && (
                <div className="flex items-center justify-between px-4 py-3 border-t border-border-light bg-bg-tertiary">
                    <p className="text-sm text-text-secondary">
                        {filteredChecks.length} çekten {((currentPage - 1) * ITEMS_PER_PAGE) + 1}-{Math.min(currentPage * ITEMS_PER_PAGE, filteredChecks.length)} arası
                    </p>
                    <div className="flex items-center gap-1">
                        <button
                            onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                            disabled={currentPage === 1}
                            className={clsx(
                                "p-2 rounded-lg transition-colors",
                                currentPage === 1 ? "text-text-muted cursor-not-allowed" : "text-text-secondary hover:bg-bg-hover"
                            )}
                        >
                            <ChevronLeft size={18} />
                        </button>
                        {Array.from({ length: totalPages }, (_, i) => i + 1).map(page => (
                            <button
                                key={page}
                                onClick={() => setCurrentPage(page)}
                                className={clsx(
                                    "w-8 h-8 rounded-lg text-sm font-medium transition-colors",
                                    currentPage === page ? "bg-primary text-white" : "text-text-secondary hover:bg-bg-hover"
                                )}
                            >
                                {page}
                            </button>
                        ))}
                        <button
                            onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                            disabled={currentPage === totalPages}
                            className={clsx(
                                "p-2 rounded-lg transition-colors",
                                currentPage === totalPages ? "text-text-muted cursor-not-allowed" : "text-text-secondary hover:bg-bg-hover"
                            )}
                        >
                            <ChevronRight size={18} />
                        </button>
                    </div>
                </div>
            )}
        </Card>
    );
}
